import React from 'react';
import { Card, Button } from 'react-bootstrap';
import type { Question, QuestionSection } from '../../types/types';
import { getLocalizedString } from '../../util/getLocalizedString';

interface Props {
  question: Question;
  section: QuestionSection;
  onEdit: () => void;
  onDelete: () => void;
  onSave: (question: Question) => void;
  onReorder: (newOrder: number, questionId: string) => void;
}

const QuestionCard: React.FC<Props> = ({
  question,
  onEdit,
  onDelete,
  onReorder,
  section,
}) => {
  const typeLabelMap: Record<string, string> = {
    text: 'Text',
    multiline: 'Long Text',
    number: 'Number',
    percentage: 'Percentage',
    single_choice: 'Single Choice',
    multi_choice: 'Multi Choice',
    boolean: 'Yes/No',
    date: 'Date',
    file: 'File',
    country: 'Country',
    industry: 'Industry',
  };

  const typeLabel = typeLabelMap[question.type] || 'Unknown';

  return (
    <Card className="mb-2" data-testid={question.id}>
      <Card.Body className="d-flex justify-content-between align-items-center">
        <div className="border-end">
          <div className="d-flex flex-column align-items-center me-2">
            <Button
              variant="link"
              size="sm"
              disabled={question.ordinal === 0}
              className="p-0"
              onClick={() =>
                onReorder((question.ordinal ?? 0) - 1, question.id)
              }
              aria-label="Move up"
            >
              <i className="bi bi-arrow-up-square"></i>
            </Button>
            <Button
              variant="link"
              size="sm"
              className="p-0"
              disabled={question.ordinal === section.questions.length - 1}
              onClick={() =>
                onReorder((question.ordinal ?? 0) + 1, question.id)
              }
              aria-label="Move down"
            >
              <i className="bi bi-arrow-down-square"></i>
            </Button>
          </div>
        </div>
        <div className="flex-grow-1 ms-2">
          <div className="fw-bold">
            {getLocalizedString(question.label)}{' '}
            {question.hidden && <i className="bi bi-eye-slash"></i>}
          </div>
          <div className="small text-muted">
            {typeLabel} {question.required && '| Required'}{' '}
            {question.hidden && '| Hidden'}
          </div>
        </div>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="me-1 editor-icon-button"
            onClick={onEdit}
          >
            <i className="bi bi-pencil"></i>
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            className="editor-icon-button"
            onClick={onDelete}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

export default QuestionCard;
